import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["onMouseEnter", "onMouseLeave", "onMouseOver", "onKeyDown", "onKeyPress", "onValueChange", "width", "error", "role", "warning", "leftIcon", "rightIcon", "borderless", "value", "align", "type", "mask", "maskChar", "alwaysShowMask", "size", "placeholder", "selectAllOnFocus", "disabled", "onUnexpectedInput", "prefix", "suffix", "formatChars", "corners", "aria-describedby", "aria-controls", "aria-label", "element"];

var _class, _class2, _temp; // TODO: Enable this rule in functional components.

/* eslint-disable @typescript-eslint/no-unused-vars */


import invariant from 'invariant';
import React from 'react';
import warning from 'warning';
import { globalObject } from '@skbkontur/global-object';
import { isEdge, isIE11 } from "../../../lib/client";
import { isKeyBackspace, isKeyDelete, someKeys } from "../../../lib/events/keyboard/identifiers";
import { needsPolyfillPlaceholder } from "../../../lib/needsPolyfillPlaceholder";
import { InternalMaskedInput } from "../../../internal/InternalMaskedInput";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { rootNode } from "../../../lib/rootNode";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { isFunction } from "../../../lib/utils";
import { styles } from "../Input.styles";
import { InputLayout } from "../InputLayout/InputLayout";
import { PolyfillPlaceholder } from "../InputLayout/PolyfillPlaceholder";
export var inputTypes = ['password', 'text', 'number', 'tel', 'search', 'time', 'date', 'url', 'email'];
/**
 * @deprecated use SizeProp
 */

export var selectionAllowedTypes = ['text', 'password', 'tel', 'search', 'url'];
export var selectionErrorMessage = function selectionErrorMessage(type, allowedTypes) {
  if (allowedTypes === void 0) {
    allowedTypes = selectionAllowedTypes;
  }

  return "<Input />. Selection is not supported by the type \"" + type + "\". Types that support selection: " + allowedTypes.map(function (i) {
    return "\"" + i + "\"";
  }).join(', ') + ". Reason: https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange.";
};
export var maskForbiddenTypes = ['number', 'date', 'time'];
export var maskAllowedTypes = inputTypes.filter(function (type) {
  return !maskForbiddenTypes.includes(type);
});
export var maskErrorMessage = function maskErrorMessage(type, allowedTypes) {
  if (allowedTypes === void 0) {
    allowedTypes = maskAllowedTypes;
  }

  return "<Input />. Prop \"mask\" does not support type \"" + type + "\". Supported types: " + allowedTypes.map(function (i) {
    return "\"" + i + "\"";
  }).join(', ') + ".";
};
export var InputDataTids = {
  root: 'Input__root'
};
export
/**
* Интерфейс пропсов наследуется от `React.InputHTMLAttributes<HTMLInputElement>`.
*  Все пропсы кроме перечисленных, `className` и `style` передаются в `<input>`
*/
var Input = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Input, _React$Component);

  function Input() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Input.defaultProps);
    _this.state = {
      needsPolyfillPlaceholder: needsPolyfillPlaceholder,
      blinking: false,
      focused: false
    };
    _this.selectAllId = null;
    _this.input = null;

    _this.selectAll = function () {
      if (_this.input) {
        _this.setSelectionRange(0, _this.input.value.length);
      }
    };

    _this.delaySelectAll = function () {
      var _globalObject$request;

      return _this.selectAllId = (_globalObject$request = globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(_this.selectAll)) != null ? _globalObject$request : null;
    };

    _this.cancelDelayedSelectAll = function () {
      if (_this.selectAllId) {
        globalObject.cancelAnimationFrame == null ? void 0 : globalObject.cancelAnimationFrame(_this.selectAllId);
        _this.selectAllId = null;
      }
    };

    _this.cancelBlink = function (callback) {
      if (_this.blinkTimeout) {
        globalObject.clearTimeout(_this.blinkTimeout);
        _this.blinkTimeout = 0;

        if (_this.state.blinking) {
          _this.setState({
            blinking: false
          }, callback);

          return;
        }
      }

      if (callback) {
        callback();
      }
    };

    _this.getInput = function (inputProps) {
      if (_this.props.element) {
        return /*#__PURE__*/React.cloneElement(_this.props.element, inputProps);
      }

      return _this.props.mask && !_this.canBeUsedWithMask ? _this.renderMaskedInput(inputProps, _this.props.mask) : /*#__PURE__*/React.createElement('input', inputProps);
    };

    _this.renderMain = function (props) {
      var _cx, _cx2;

      var onMouseEnter = props.onMouseEnter,
          onMouseLeave = props.onMouseLeave,
          onMouseOver = props.onMouseOver,
          onKeyDown = props.onKeyDown,
          onKeyPress = props.onKeyPress,
          onValueChange = props.onValueChange,
          width = props.width,
          error = props.error,
          role = props.role,
          warning = props.warning,
          leftIcon = props.leftIcon,
          rightIcon = props.rightIcon,
          borderless = props.borderless,
          value = props.value,
          align = props.align,
          type = props.type,
          mask = props.mask,
          maskChar = props.maskChar,
          alwaysShowMask = props.alwaysShowMask,
          size = props.size,
          placeholder = props.placeholder,
          selectAllOnFocus = props.selectAllOnFocus,
          disabled = props.disabled,
          onUnexpectedInput = props.onUnexpectedInput,
          prefix = props.prefix,
          suffix = props.suffix,
          formatChars = props.formatChars,
          corners = props.corners,
          ariaDescribedby = props['aria-describedby'],
          ariaControls = props['aria-controls'],
          ariaLabel = props['aria-label'],
          element = props.element,
          rest = _objectWithoutPropertiesLoose(props, _excluded);

      var _this$state = _this.state,
          blinking = _this$state.blinking,
          focused = _this$state.focused;
      var labelProps = {
        className: cx(styles.root(_this.theme), _this.getSizeClassName(), (_cx = {}, _cx[styles.focus(_this.theme)] = focused && !warning && !error, _cx[styles.hovering(_this.theme)] = !focused && !disabled && !warning && !error && !borderless, _cx[styles.blink(_this.theme)] = blinking, _cx[styles.borderless()] = borderless && !focused, _cx[styles.disabled(_this.theme)] = disabled, _cx[styles.warning(_this.theme)] = warning, _cx[styles.error(_this.theme)] = error, _cx[styles.focusFallback(_this.theme)] = focused && (isIE11 || isEdge), _cx[styles.warningFallback(_this.theme)] = warning && (isIE11 || isEdge), _cx[styles.errorFallback(_this.theme)] = error && (isIE11 || isEdge), _cx)),
        'aria-controls': ariaControls,
        style: _extends({
          width: width
        }, corners),
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onMouseOver: onMouseOver
      };

      var inputProps = _extends({}, rest, {
        className: cx(styles.input(_this.theme), (_cx2 = {}, _cx2[styles.inputFocus(_this.theme)] = focused, _cx2[styles.inputDisabled(_this.theme)] = disabled, _cx2)),
        value: value,
        role: role,
        onChange: _this.handleChange,
        onFocus: _this.handleFocus,
        onKeyDown: _this.handleKeyDown,
        onKeyPress: _this.handleKeyPress,
        onBlur: _this.handleBlur,
        style: {
          textAlign: align
        },
        ref: _this.refInput,
        type: type,
        placeholder: !_this.isMaskVisible && !needsPolyfillPlaceholder ? placeholder : undefined,
        disabled: disabled,
        'aria-describedby': ariaDescribedby,
        'aria-label': ariaLabel
      });

      var input = _this.getInput(inputProps);

      if (isTheme2022(_this.theme)) {
        return /*#__PURE__*/React.createElement(InputLayout, {
          leftIcon: leftIcon,
          rightIcon: rightIcon,
          prefix: prefix,
          suffix: suffix,
          labelProps: labelProps,
          context: {
            disabled: Boolean(disabled),
            focused: focused,
            size: size
          }
        }, input, _this.state.needsPolyfillPlaceholder && /*#__PURE__*/React.createElement(PolyfillPlaceholder, {
          isMaskVisible: _this.isMaskVisible,
          value: value,
          defaultValue: _this.props.defaultValue,
          align: align
        }, placeholder));
      }

      return /*#__PURE__*/React.createElement("label", _extends({
        "data-tid": InputDataTids.root
      }, labelProps), /*#__PURE__*/React.createElement("span", {
        className: styles.sideContainer()
      }, _this.renderLeftIcon(), _this.renderPrefix()), /*#__PURE__*/React.createElement("span", {
        className: styles.wrapper()
      }, input, _this.renderPlaceholder()), /*#__PURE__*/React.createElement("span", {
        className: cx(styles.sideContainer(), styles.rightContainer())
      }, _this.renderSuffix(), _this.renderRightIcon()));
    };

    _this.refInput = function (element) {
      if (element instanceof InternalMaskedInput || element && 'input' in element) {
        _this.input = element.input;
      } else {
        _this.input = element;
      }
    };

    _this.handleChange = function (event) {
      if (needsPolyfillPlaceholder) {
        var fieldIsEmpty = event.target.value === '';

        if (_this.state.needsPolyfillPlaceholder !== fieldIsEmpty) {
          _this.setState({
            needsPolyfillPlaceholder: fieldIsEmpty
          });
        }
      }

      if (_this.props.onValueChange) {
        _this.props.onValueChange(event.target.value);
      }

      if (_this.props.onChange) {
        _this.props.onChange(event);
      }
    };

    _this.handleFocus = function (event) {
      _this.setState({
        focused: true
      });

      if (_this.props.selectAllOnFocus) {
        _this.input && !isIE11 ? _this.selectAll() : _this.delaySelectAll();
      }

      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
    };

    _this.handleKeyDown = function (e) {
      if (_this.props.onKeyDown) {
        _this.props.onKeyDown(e);
      }

      var isDeleteKey = someKeys(isKeyBackspace, isKeyDelete)(e);

      if (!e.currentTarget.value && isDeleteKey && !e.repeat) {
        _this.handleUnexpectedInput();
      }
    };

    _this.handleKeyPress = function (event) {
      if (_this.props.onKeyPress) {
        _this.props.onKeyPress(event);
      }

      if (_this.props.maxLength === event.currentTarget.value.length) {
        _this.handleUnexpectedInput(event.currentTarget.value);
      }
    };

    _this.handleMaskedValueChange = function (value) {
      if (_this.props.onValueChange) {
        _this.props.onValueChange(value);
      }
    };

    _this.handleUnexpectedInput = function (value) {
      if (value === void 0) {
        value = _this.props.value || '';
      }

      if (_this.props.onUnexpectedInput) {
        _this.props.onUnexpectedInput(value);
      } else {
        _this.blink();
      }
    };

    _this.handleBlur = function (event) {
      _this.setState({
        focused: false
      });

      if (_this.props.onBlur) {
        _this.props.onBlur(event);
      }
    };

    _this.renderPrefix = function () {
      var _cx3;

      var _this$props = _this.props,
          prefix = _this$props.prefix,
          disabled = _this$props.disabled;

      if (!prefix) {
        return null;
      }

      return /*#__PURE__*/React.createElement("span", {
        className: cx(styles.prefix(_this.theme), (_cx3 = {}, _cx3[styles.prefixDisabled(_this.theme)] = disabled, _cx3))
      }, prefix);
    };

    _this.renderSuffix = function () {
      var _cx4;

      var _this$props2 = _this.props,
          suffix = _this$props2.suffix,
          disabled = _this$props2.disabled;

      if (!suffix) {
        return null;
      }

      return /*#__PURE__*/React.createElement("span", {
        className: cx(styles.suffix(_this.theme), (_cx4 = {}, _cx4[styles.suffixDisabled(_this.theme)] = disabled, _cx4))
      }, suffix);
    };

    return _this;
  }

  var _proto = Input.prototype;

  _proto.outputMaskError = function outputMaskError() {
    warning(!(this.props.mask && this.canBeUsedWithMask), maskErrorMessage(this.getProps().type));
  };

  _proto.componentDidMount = function componentDidMount() {
    this.outputMaskError();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (this.props.type !== prevProps.type || this.props.mask !== prevProps.mask) {
      this.outputMaskError();
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.blinkTimeout) {
      globalObject.clearTimeout(this.blinkTimeout);
    }

    this.cancelDelayedSelectAll();
  }
  /**
  * @public
  */
  ;

  _proto.focus = function focus() {
    invariant(this.input, 'Cannot call "focus" because Input is not mounted');
    this.input.focus();
  }
  /**
  * @public
  */
  ;

  _proto.blur = function blur() {
    invariant(this.input, 'Cannot call "blur" because Input is not mounted');
    this.input.blur();
  }
  /**
  * @public
  */
  ;

  _proto.getNode = function getNode() {
    return this.input;
  }
  /**
  * @public
  */
  ;

  _proto.blink = function blink() {
    var _this2 = this;

    if (this.blinkTimeout) {
      this.cancelBlink(function () {
        // trigger reflow to restart animation
        // @see https://css-tricks.com/restart-css-animation/#article-header-id-0
        void (_this2.input && _this2.input.offsetWidth);

        _this2.blink();
      });
      return;
    }

    this.setState({
      blinking: true
    }, function () {
      _this2.blinkTimeout = globalObject.setTimeout(_this2.cancelBlink, 150);
    });
  }
  /**
  * @public
  * @param {number} start
  * @param {number} end
  */
  ;

  _proto.setSelectionRange = function setSelectionRange(start, end) {
    var _globalObject$documen,
        _this$props$value,
        _this3 = this; // https://github.com/facebook/react/issues/7769
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange


    if (!selectionAllowedTypes.includes(this.getProps().type)) {
      warning(false, selectionErrorMessage(this.getProps().type));
      return;
    }

    if (!this.input) {
      throw new Error('Cannot call "setSelectionRange" on unmounted Input');
    }

    if (((_globalObject$documen = globalObject.document) == null ? void 0 : _globalObject$documen.activeElement) !== this.input) {
      this.focus();
    }

    if (this.props.mask && this.props.value && ((_this$props$value = this.props.value) == null ? void 0 : _this$props$value.length) < this.props.mask.length) {
      globalObject.setTimeout(function () {
        var _this3$input;

        (_this3$input = _this3.input) == null ? void 0 : _this3$input.setSelectionRange(start, end);
      }, 150);
    } else {
      var _this$input;

      (_this$input = this.input) == null ? void 0 : _this$input.setSelectionRange(start, end);
    }
  };

  _proto.render = function render() {
    var _this4 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this4.theme = theme;
      return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
        rootNodeRef: _this4.setRootNode
      }, _this4.props), _this4.renderMain);
    });
  };

  _proto.renderMaskedInput = function renderMaskedInput(inputProps, mask) {
    return /*#__PURE__*/React.createElement(InternalMaskedInput, _extends({}, inputProps, {
      mask: mask,
      maskChar: this.props.maskChar,
      alwaysShowMask: this.props.alwaysShowMask,
      formatChars: this.props.formatChars,
      onChange: this.props.onChange,
      onValueChange: this.handleMaskedValueChange,
      onUnexpectedInput: this.handleUnexpectedInput
    }));
  };

  _proto.getIconSizeClassname = function getIconSizeClassname(right) {
    if (right === void 0) {
      right = false;
    }

    switch (this.getProps().size) {
      case 'large':
        return right ? styles.rightIconLarge(this.theme) : styles.leftIconLarge(this.theme);

      case 'medium':
        return right ? styles.rightIconMedium(this.theme) : styles.leftIconMedium(this.theme);

      case 'small':
      default:
        return right ? styles.rightIconSmall(this.theme) : styles.leftIconSmall(this.theme);
    }
  };

  _proto.renderLeftIcon = function renderLeftIcon() {
    return this.renderIcon(this.props.leftIcon, this.getIconSizeClassname());
  };

  _proto.renderRightIcon = function renderRightIcon() {
    return this.renderIcon(this.props.rightIcon, this.getIconSizeClassname(true));
  };

  _proto.renderIcon = function renderIcon(icon, sizeClassName) {
    var _cx5;

    if (!icon) {
      return null;
    }

    var disabled = this.props.disabled;
    var iconNode = isFunction(icon) ? icon() : icon;
    return /*#__PURE__*/React.createElement("span", {
      className: cx(styles.icon(), sizeClassName, styles.useDefaultColor(this.theme), (_cx5 = {}, _cx5[styles.iconFocus(this.theme)] = this.state.focused, _cx5[styles.iconDisabled()] = disabled, _cx5))
    }, iconNode);
  };

  _proto.renderPlaceholder = function renderPlaceholder() {
    var disabled = this.props.disabled;
    var focused = this.state.focused;
    var placeholder = null;

    if (this.state.needsPolyfillPlaceholder && this.props.placeholder && !this.isMaskVisible && !this.props.value && !this.props.defaultValue) {
      var _cx6;

      placeholder = /*#__PURE__*/React.createElement("div", {
        className: cx(styles.placeholder(this.theme), (_cx6 = {}, _cx6[styles.placeholderDisabled(this.theme)] = disabled, _cx6[styles.placeholderFocus(this.theme)] = focused, _cx6)),
        style: {
          textAlign: this.props.align || 'inherit'
        }
      }, this.props.placeholder);
    }

    return placeholder;
  };

  _proto.getSizeClassName = function getSizeClassName() {
    var _cx7, _cx8, _cx9;

    switch (this.getProps().size) {
      case 'large':
        return cx((_cx7 = {}, _cx7[styles.sizeLarge(this.theme)] = true, _cx7[styles.sizeLargeFallback(this.theme)] = isIE11 || isEdge, _cx7));

      case 'medium':
        return cx((_cx8 = {}, _cx8[styles.sizeMedium(this.theme)] = true, _cx8[styles.sizeMediumFallback(this.theme)] = isIE11 || isEdge, _cx8));

      case 'small':
      default:
        return cx((_cx9 = {}, _cx9[styles.sizeSmall(this.theme)] = true, _cx9[styles.sizeSmallFallback(this.theme)] = isIE11 || isEdge, _cx9));
    }
  };

  _createClass(Input, [{
    key: "isMaskVisible",
    get: function get() {
      var _this$props3 = this.props,
          mask = _this$props3.mask,
          alwaysShowMask = _this$props3.alwaysShowMask;
      var focused = this.state.focused;
      return Boolean(mask && (focused || alwaysShowMask));
    }
  }, {
    key: "canBeUsedWithMask",
    get: function get() {
      return maskForbiddenTypes.includes(this.getProps().type);
    }
    /**
    * Работает с типами `text`, `password`, `tel`, `search`, `url`
    * [Документация](https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange)
    * @public
    */

  }]);

  return Input;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'Input', _class2.displayName = 'Input', _class2.defaultProps = {
  size: 'small',
  type: 'text'
}, _temp)) || _class;